import { AutocompleteEmptyState } from "@/features/shop/components/SearchBarAutocomplete/AutocompleteEmptyState";
import { AutocompleteItem } from "@/features/shop/components/SearchBarAutocomplete/AutocompleteItem";
import type { AutocompleteResult } from "@/features/shop/types/autocomplete.types";

type AutocompleteDropdownProps = {
  results: AutocompleteResult[]
  isOpen: boolean
  isLoading: boolean
  hasQuery: boolean
  isQueryValid: boolean
  highlightedIndex: number
  onSelect: (result: AutocompleteResult) => void
  onHighlight: (index: number) => void
};

const MAX_VISIBLE_ITEMS = 8;
const ITEM_HEIGHT_PX = 60;
const MAX_HEIGHT_PX = MAX_VISIBLE_ITEMS * ITEM_HEIGHT_PX;

export const AutocompleteDropdown = ({
  results,
  isOpen,
  isLoading,
  hasQuery,
  isQueryValid,
  highlightedIndex,
  onSelect,
  onHighlight,
}: AutocompleteDropdownProps) => {
  if (!isOpen) {
    return null;
  }

  const showEmptyState = (isQueryValid && results.length === 0)
    || isLoading
    || (!isQueryValid && hasQuery);

  if (showEmptyState) {
    return (
      <div className="absolute top-full left-0 right-0 z-50 mt-2 overflow-hidden rounded-xl border-0 bg-white shadow-xl dark:bg-gray-800 animate-fade-in">
        <AutocompleteEmptyState
          isLoading={isLoading}
          hasQuery={hasQuery}
          isQueryValid={isQueryValid}
        />
      </div>
    );
  }

  return (
    <div className="absolute top-full left-0 right-0 z-50 mt-2 overflow-hidden rounded-xl border-0 bg-white shadow-xl dark:bg-gray-800 animate-fade-in">
      <ul
        role="listbox"
        className="overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 dark:hover:scrollbar-thumb-gray-500"
        style={{ maxHeight: `${MAX_HEIGHT_PX}px` }}
      >
        {results.map((result, index) => (
          <li key={result.id} role="option" aria-selected={index === highlightedIndex}>
            <AutocompleteItem
              result={result}
              isHighlighted={index === highlightedIndex}
              onSelect={onSelect}
              onMouseEnter={() => onHighlight(index)}
            />
            {index < results.length - 1 && (
              <div className="mx-4 h-px bg-gray-100 dark:bg-gray-700/50" />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
